/**
 * Assert utility for catching programmer issues during development.
 * Throws an error if the condition is false.
 *
 * @param condition - The condition to check
 * @param message - Error message to throw if condition is false
 * @throws {Error} When condition is false
 *
 * @example
 * // Instead of:
 * // if (!playerRef.current) {
 * //   throw new Error("Player not initialized");
 * // }
 *
 * // Use:
 * assert(playerRef.current, "Player not initialized");
 */
export function assert(condition: unknown, message: string): asserts condition {
  if (!condition) {
    throw new Error(`Assertion failed: ${message}`);
  }
}

/**
 * Assert that a value is not null or undefined.
 * Provides better type narrowing for nullable values.
 *
 * @param value - The value to check
 * @param message - Error message if value is null/undefined
 * @throws {Error} When value is null or undefined
 *
 * @example
 * assertDefined(user, "User must be logged in");
 * // Now TypeScript knows user is not null/undefined
 */
export function assertDefined<T>(
  value: T | null | undefined,
  message: string
): asserts value is T {
  if (value == null) {
    throw new Error(`Assertion failed: ${message}`);
  }
}

/**
 * Assert that a condition should never happen.
 * Useful for exhaustive checks and unreachable code.
 *
 * @param value - The value that should never exist
 * @param message - Error message describing what shouldn't happen
 * @throws {Error} Always throws
 *
 * @example
 * switch (status) {
 *   case 'loading':
 *     return <Spinner />;
 *   case 'success':
 *     return <Data />;
 *   case 'error':
 *     return <Error />;
 *   default:
 *     assertNever(status, `Unhandled status: ${status}`);
 * }
 */
export function assertNever(value: never, message?: string): never {
  const errorMessage = message || `Unexpected value: ${value}`;
  throw new Error(`Assertion failed: ${errorMessage}`);
}

// Export default for easy importing
export default assert;
